import type { Entity as WorkbookEntity } from '@mathflat/domain/@entities/(Content)/Workbook/dto'
import { CoverData } from '@mathflat/domain/@entities/DesignTemplate/CoverData/dto'
import type { PODApi as PODMallCartItemApi } from '@mathflat/domain/@entities/PODMall/CartItem/api'
import type { Entity as CartItemEntity } from '@mathflat/domain/@entities/PODMall/CartItem/dto'
import {
  POD_PAYMENT_PAYER_TYPE,
  type PodPaymentPayerType,
} from '@mathflat/domain/@entities/PODMall/Payment/type'
import type { PrintDocumentCoverDataT } from '@mathflat/domain/@entities/PrintDocument/type'
import { action, computed, makeObservable, observable } from 'mobx'

import { DTO } from '@/dto'

import { CartItemStudent } from './CartItemStudent'

export class CartItem extends DTO implements CartItemEntity.CartItem {
  id: CartItemEntity.CartItem['id']
  customSignatureWorkbookId?: CartItemEntity.CartItem['customSignatureWorkbookId']
  customWorkbookId?: CartItemEntity.CartItem['customWorkbookId']
  minQuantity: CartItemEntity.CartItem['minQuantity']
  signatureType: CartItemEntity.CartItem['signatureType']
  productType: CartItemEntity.CartItem['productType']
  paymentPayerType: PodPaymentPayerType
  pdfType: CartItemEntity.CartItem['pdfType']
  printType: CartItemEntity.CartItem['printType']
  showPrice: CartItemEntity.CartItem['showPrice']
  autoScored: CartItemEntity.CartItem['autoScored']
  name: CartItemEntity.CartItem['name']
  pairFlag: CartItemEntity.CartItem['pairFlag']
  pageCount: CartItemEntity.CartItem['pageCount']
  problemPageCount: CartItemEntity.CartItem['problemPageCount']
  problemAnswerPageCount: CartItemEntity.CartItem['problemAnswerPageCount']
  price: CartItemEntity.CartItem['price']
  additionalPrice: CartItemEntity.CartItem['additionalPrice']
  thumbnailImageUrl: CartItemEntity.CartItem['thumbnailImageUrl']
  productId: CartItemEntity.CartItem['productId']
  coverData: CartItemEntity.CartItem['coverData'] = null
  innerPdfUrl: CartItemEntity.CartItem['innerPdfUrl']
  discountAmount: CartItemEntity.CartItem['discountAmount']
  productDiscountRate: CartItemEntity.CartItem['productDiscountRate']
  discountedPrice: CartItemEntity.CartItem['discountedPrice']
  productAmount: CartItemEntity.CartItem['productAmount']
  grade: CartItemEntity.CartItem['grade']
  semester: CartItemEntity.CartItem['semester']
  revision: CartItemEntity.CartItem['revision']
  schoolType: CartItemEntity.CartItem['schoolType']

  parentOrders: CartItemEntity.CartItem['parentOrders'] = null
  students: CartItemStudent[] = []

  get studentNames() {
    return this.students.map((student) => student.name)
  }

  get filledStudentNames() {
    return this.students.filter((student) => student.name !== '')
  }

  get quantity() {
    return this.students.length
  }

  constructor(
    props:
      | (CartItemEntity.CartItem & {
          students?: CartItemStudent[]
        })
      | (PODMallCartItemApi.Response.CartItems['cartItems'][number] & {
          students?: CartItemStudent[]
        }),
  ) {
    super()

    makeObservable(this, {
      id: observable,
      customSignatureWorkbookId: observable,
      customWorkbookId: observable,
      signatureType: observable,
      productType: observable,
      pdfType: observable,
      printType: observable,
      showPrice: observable,
      autoScored: observable,
      name: observable,
      pairFlag: observable,
      pageCount: observable,
      problemPageCount: observable,
      problemAnswerPageCount: observable,
      price: observable,
      additionalPrice: observable,
      thumbnailImageUrl: observable,
      minQuantity: observable,
      productId: observable,
      coverData: observable,
      innerPdfUrl: observable,
      discountAmount: observable,
      productDiscountRate: observable,
      discountedPrice: observable,
      productAmount: observable,

      grade: observable,
      semester: observable,
      revision: observable,
      schoolType: observable,

      totalPrice: computed,
      getUpdateParams: action,

      students: observable,
      parentOrders: observable,
      studentNames: computed,
      quantity: computed,
    })

    this.id = props.id
    this.customSignatureWorkbookId = props.customSignatureWorkbookId
    this.customWorkbookId = props.customWorkbookId
    this.minQuantity = props.minQuantity
    this.productType = props.productType
    this.paymentPayerType = props.paymentPayerType
    this.pdfType = props.pdfType
    this.printType = props.printType
    this.signatureType = props.signatureType
    this.showPrice = props.showPrice
    this.autoScored = props.autoScored
    this.name = props.name
    this.pairFlag = props.pairFlag
    this.pageCount = props.pageCount
    this.problemPageCount = props.problemPageCount
    this.problemAnswerPageCount = props.problemAnswerPageCount
    this.price = props.price
    this.additionalPrice = props.additionalPrice
    this.productDiscountRate = props.productDiscountRate
    this.discountedPrice = props.discountedPrice
    this.productAmount = props.productAmount
    this.thumbnailImageUrl = props.thumbnailImageUrl
    this.productId = props.productId
    if (props.coverData) {
      this.coverData = new CoverData<
        WorkbookEntity.CustomSignatureWorkbookCoverDataT | PrintDocumentCoverDataT
      >(props.coverData)
    }
    this.innerPdfUrl = props.innerPdfUrl
    this.discountAmount = props.discountAmount

    this.grade = props.grade
    this.semester = props.semester
    this.revision = props.revision
    this.schoolType = props.schoolType

    this.parentOrders = props.parentOrders
    this.students =
      props.paymentPayerType === 'PARENT_PAYMENT' && props.parentOrders
        ? props.parentOrders.map(
            (parentOrder) =>
              new CartItemStudent({
                studentId: parentOrder.studentId,
                name: parentOrder.studentName,
                printingUserName: parentOrder.printingUserName,
                parentPhoneNumber: parentOrder.parentPhoneNumber,
              }),
          )
        : props.studentNames.map((studentName) => new CartItemStudent({ name: studentName }))
  }

  get priceWithAdditionalPrice() {
    return this.price + (this.additionalPrice ?? 0)
  }

  get totalPrice() {
    return this.productAmount - this.discountAmount
  }

  getUpdateParams(
    paymentPayerType: PodPaymentPayerType,
  ):
    | PODMallCartItemApi.Request.SignatureWorkbookCartItemModification
    | PODMallCartItemApi.Request.CustomWorkbookCartItemModification
    | PODMallCartItemApi.Request.PrintDocumentCartItemModification {
    return {
      customSignatureWorkbookId: this.customSignatureWorkbookId,
      productType: this.productType,
      additionalPrice: this.additionalPrice,
      quantity: this.quantity,
      pdfType: this.pdfType,
      printType: this.printType,
      studentNames: this.studentNames,
      productId: this.productId,
      parentOrders:
        paymentPayerType === POD_PAYMENT_PAYER_TYPE.PARENT_PAYMENT
          ? this.students.map((student) => student.toParentOrders())
          : null,
    }
  }
}
